import { Carousel, Card, Avatar, Typography, Progress, Flex } from "antd";
import { useRef, useState } from "react"; // ✅ move inside component
import { LeftOutlined, RightOutlined } from "@ant-design/icons"; // ✅ import icons
import sliderBg from "../assets/images/review-bg.webp";
import ReviewCard from "./ReviewsCardSlider";

const { Paragraph } = Typography;

const chunkArray = (arr, size) =>
  arr.reduce((acc, _, i) => {
    if (i % size === 0) acc.push(arr.slice(i, i + size));
    return acc;
  }, []);

const ReviewSlider = ({
  reviews = [],
  ratingLevels = [],
  totalRatings = 0,
  averageRating = 0,
  totalCountries = 65,
}) => {
  const carouselRef = useRef(null);
  const [current, setCurrent] = useState(0);

  const handlePrev = () => {
    if (carouselRef.current) {
      carouselRef.current.prev(); // ✅ only call if ref exists
    }
  };

  const handleNext = () => {
    if (carouselRef.current) {
      carouselRef.current.next(); // ✅ only call if ref exists
    }
  };

  const desktopSlides = chunkArray(reviews, 2);
  const mobileSlides = chunkArray(reviews, 1);

  // star position mapping (like your design)
  const starPositions = [
    { stars: 1, className: "absolute top-0 flex flex-col items-center" },
    {
      stars: 2,
      className: "absolute top-[20%] left-[30%] flex flex-col items-center",
    },
    {
      stars: 3,
      className: "absolute top-[20%] right-[30%] flex flex-col items-center",
    },
    {
      stars: 4,
      className: "absolute bottom-[30%] left-[22%] flex flex-col items-center",
    },
    {
      stars: 5,
      className: "absolute bottom-[30%] right-[22%] flex flex-col items-center",
    },
  ];

  // get count for each star dynamically
  const getCount = (stars) =>
    ratingLevels?.find((item) => item.stars === stars)?.count || "0";

  return (
    <div
      className="relative w-full min-h-[520px] bg-cover bg-center px-4"
      style={{ backgroundImage: `url(${sliderBg})` }}
    >
      <div className="hidden md:block">
        {/* ⭐ Ratings Circle */}
        <div className="relative top-10 flex items-center justify-center w-1/2 h-[100px] mx-auto pt-5">
          {/* <!-- Center Circle --> */}
          <div className="absolute flex flex-col items-center justify-center gap-1">
            <div className="shadow-xl flex items-center justify-center flex-col w-24 h-24 rounded-full border border-white text-white text-center">
              <span className="text-2xl drop-shadow-[0_4px_6px_rgba(0,0,0,0.25)]">
                ★
              </span>
              <span className="text-xs font-medium">
                {averageRating?.toFixed(1)} ({totalRatings})
              </span>
            </div>
            <Paragraph className="text-sm mt-1 text-white font-medium drop-shadow-md">
              From {totalCountries}+ countries
            </Paragraph>
          </div>

          {/* <!-- Supper Top Star 1 --> */}
          {getCount(1) != 0 && (
            <div className="absolute top-0 flex flex-col items-center">
              <div className="shadow-xl flex items-center justify-center w-20 h-20 rounded-full border border-white">
                <span className="text-white text-6xl font-bold absolute -top-7">
                  <span
                    className="absolute left-0 right-0 top-[19px] mx-auto flex items-center justify-center
               text-primary text-center text-lg font-semibold z-10"
                  >
                    1
                  </span>
                  <span className="drop-shadow-[0_4px_6px_rgba(0,0,0,0.25)]">
                    ★
                  </span>
                </span>
                <span className="text-white mt-1">{getCount(1)}</span>
              </div>
            </div>
          )}
          {/* <!-- Top Star 2 --> */}
          {getCount(2) != 0 && (
            <div className="absolute top-[20%] left-[30%] flex flex-col items-center">
              <div className="shadow-xl flex items-center justify-center w-20 h-20 rounded-full border border-white">
                <span className="text-white text-6xl font-bold absolute -top-7">
                  <span
                    className="absolute left-0 right-0 top-[19px] mx-auto flex items-center justify-center
               text-primary text-center text-lg font-semibold z-10"
                  >
                    2
                  </span>
                  <span className="drop-shadow-[0_4px_6px_rgba(0,0,0,0.25)]">
                    ★
                  </span>
                </span>
                <span className="text-white mt-1">{getCount(2)}</span>
              </div>
            </div>
          )}

          {/* <!-- Top Star 3 --> */}
          {getCount(3) != 0 && (
            <div className="absolute top-[20%] right-[30%] flex flex-col items-center">
              <div className="shadow-xl flex items-center justify-center w-20 h-20 rounded-full border border-white">
                <span className="text-white text-6xl font-bold absolute -top-7">
                  <span
                    className="absolute left-0 right-0 top-[19px] mx-auto flex items-center justify-center
               text-primary text-center text-lg font-semibold z-10"
                  >
                    3
                  </span>
                  <span className="drop-shadow-[0_4px_6px_rgba(0,0,0,0.25)]">
                    ★
                  </span>
                </span>
                <span className="text-white mt-1">{getCount(3)}</span>
              </div>
            </div>
          )}

          {/* <!-- Bellow Star 4 --> */}
          {getCount(4) != 0 && (
            <div className="absolute bottom-[30%] left-[22%] flex flex-col items-center">
              <div className="shadow-xl flex items-center justify-center w-20 h-20 rounded-full border border-white">
                <span className="text-white text-6xl font-bold absolute -top-7">
                  <span
                    className="absolute left-0 right-0 top-[19px] mx-auto flex items-center justify-center
               text-primary text-center text-lg font-semibold z-10"
                  >
                    4
                  </span>
                  <span className="drop-shadow-[0_4px_6px_rgba(0,0,0,0.25)]">
                    ★
                  </span>
                </span>
                <span className="text-white mt-1">{getCount(4)}</span>
              </div>
            </div>
          )}

          {/* <!-- Bellow Star 5 --> */}
          {getCount(5) != 0 && (
            <div className="absolute bottom-[30%] right-[22%] flex flex-col items-center">
              <div className="shadow-xl flex items-center justify-center w-20 h-20 rounded-full border border-white">
                <span className="text-white text-6xl font-bold absolute -top-7">
                  <span
                    className="absolute left-0 right-0 top-[19px] mx-auto flex items-center justify-center
               text-primary text-center text-lg font-semibold z-10"
                  >
                    5
                  </span>
                  <span className="drop-shadow-[0_4px_6px_rgba(0,0,0,0.25)]">
                    ★
                  </span>
                </span>
                <span className="text-white mt-1">{getCount(5)}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Bar Ratings */}
      <div className="md:hidden bg-white rounded-xl shadow-md p-4">
        <h3 className="font-semibold text-lg mb-2">Reviews ({totalRatings})</h3>

        <div className="flex items-center gap-2 mb-4">
          <span className="text-pink-600 text-2xl font-bold">
            {averageRating.toFixed(1)}
          </span>
          <span className="text-gray-500 text-sm">{totalRatings} ratings</span>
        </div>

        <div className="space-y-2">
          {ratingLevels.map((r) => {
            const count = r.count || 0;
            const percent = totalRatings ? (count / totalRatings) * 100 : 0;
            return (
              <div key={r.stars} className="flex items-center gap-2">
                <span className="text-pink-600 text-sm w-6">{r.stars}★</span>
                <Progress
                  percent={percent}
                  showInfo={false}
                  strokeColor="#EC4899"
                  trailColor="#f3f4f6"
                  className="flex-1"
                />
                <span className="text-gray-700 text-sm">{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Desktop Slider */}
      <div className="hidden md:block absolute bottom-10 left-1/2 -translate-x-1/2 container">
        {/* Left Arrow */}
        <button
          onClick={handlePrev}
          className="w-[40px] h-[40px] absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-white shadow-md rounded-full p-2 hover:bg-primary hover:text-white transition"
        >
          <LeftOutlined />
        </button>
        <Carousel
        className="w-[90%] mx-auto h-full"
          ref={carouselRef} // attach ref here
          autoplay={false}
          dots={false}
          afterChange={(idx) => setCurrent(idx)}
          responsive={[
            { breakpoint: 1024, settings: { slidesToShow: 3 } },
            { breakpoint: 768, settings: { slidesToShow: 3 } },
            { breakpoint: 480, settings: { slidesToShow: 1 } },
          ]}
        >
          {desktopSlides.map((group, i) => (
            <div key={i} className="h-full">
              <div className="grid grid-cols-2 gap-6 h-full">
                {group.map((review, index) => (
                  <Card
                    key={index}
                    className="rounded-2xl shadow-lg overflow-visible mt-12"
                    bodyStyle={{ padding: "1.5rem", height: "100%" }}
                  >
                    <div className="flex items-center gap-2 mb-4">
                      <Avatar
                        size={41}
                        src={
                          `${process.env.REACT_APP_API_URL}${review.reviewer_image?.url}` ||
                          "https://i.pravatar.cc/100"
                        }
                      />
                      <div>
                        <h4 className="font-medium">{review.reviewer_name}</h4>
                        <Paragraph className="text-[11px] font-medium text-[#8C95A7] !mb-0">
                          Reviewed: {review.review_date}
                        </Paragraph>
                      </div>
                    </div>

                    <div
                      className={`bg-primary absolute top-[-40px] right-0 rounded-full shadow-lg text-white text-sm flex  justify-center w-[80px] h-[80px]`}
                    >
                      <div className="text-center leading-tight mt-2">
                        <div className="text-3xl">★</div>
                        <div className="text-xs font-normal">4.9 (523)</div>
                      </div>
                    </div>

                    {/* <Flex justify="center" gap={40}>
                    <Paragraph className="flex-[2] text-xs font-medium text-justify">
                      {review.review_text?.[0]?.children?.[0]?.text || ""}
                    </Paragraph>
                    <div className="relative flex-1">
                      {review.review_image?.url && (
                        <img
                          alt={review.review_image?.name || "review"}
                           src={`${process.env.REACT_APP_API_URL}${review.review_image.url}`}
                          className="w-80 h-full object-cover rounded-xl"
                        />
                      )}
                    </div>
                  </Flex> */}

                    <ReviewCard review={review} />
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </Carousel>
        <button
          onClick={handleNext}
          className="w-[40px] h-[40px] absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-white shadow-md rounded-full p-2 hover:bg-primary hover:text-white transition"
        >
          <RightOutlined />
        </button>
      </div>

      {/* Mobile Slider */}
      <div className="md:hidden absolute bottom-10 left-1/2 -translate-x-1/2 w-11/12">
        <Carousel autoplay dots={false}>
          {mobileSlides.map((group, i) => (
            <div key={i}>
              {group.map((review, index) => (
                <Card
                  key={index}
                  className="rounded-2xl shadow-lg overflow-visible mt-12"
                  bodyStyle={{ padding: "1.5rem" }}
                >
                  <div className="flex items-center gap-2 mb-4">
                    <Avatar
                      size={41}
                      src={
                        review.review_image?.url || "https://i.pravatar.cc/100"
                      }
                    />
                    <div>
                      <h3 className="font-medium text-xs">
                        {review.reviewer_name}
                      </h3>
                      <Paragraph className="text-[11px] font-medium text-[#8C95A7] !mb-0">
                        Reviewed: {review.review_date}
                      </Paragraph>
                    </div>
                  </div>

                  <Flex justify="center" gap={40}>
                    <Paragraph className="flex-[2] text-xs font-medium text-justify">
                      {review.review_text?.[0]?.children?.[0]?.text || ""}
                    </Paragraph>
                    <div className="relative flex-1">
                      {review.review_image?.url && (
                        <img
                          alt={review.review_image?.name || "review"}
                          src={review.review_image.url}
                          className="w-80 h-full object-cover rounded-xl"
                        />
                      )}
                    </div>
                  </Flex>
                </Card>
              ))}
            </div>
          ))}
        </Carousel>
      </div>
    </div>
  );
};

export default ReviewSlider;
